import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect, useMemo } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseDatePicker, BaseRow, BaseSelect } from '~/components/common';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '~/redux/hooks';

import { IEditTask, IFranchiseTask } from '~/utils/interface/task';
import { handleCreateTaskFranchise, handleUpdateFranchiseTask } from '~/thunks/task/taskThunk';
import { taskActions } from '~/thunks/task/taskSlice';
import dayjs from 'dayjs';

type EditFranchiseTaskDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: IFranchiseTask | null | IEditTask;
  children?: React.ReactNode;
};

const initialValues: IFranchiseTask = {
  name: '',
  description: '',
  dueDate: '',
};

const EditFranchiseTaskDialog = (props: EditFranchiseTaskDialogProps) => {
  const { mode = DIALOG_MODE_CREATE, data } = props;
  const { t } = useTranslation(['admin/task']);
  const statusOptions = useMemo(
    () => [
      {
        id: '1',
        name: t('selectStatus.statusTask.new', { ns: 'admin/component' }),
      },
      {
        id: '2',
        name: t('selectStatus.statusTask.progress', { ns: 'admin/component' }),
      },
      {
        id: '3',
        name: t('selectStatus.statusTask.completed', { ns: 'admin/component' }),
      },
    ],
    [t]
  );

  const dispatch = useAppDispatch();
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateTaskFranchise({ data: values })).unwrap();
          dispatch(taskActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleUpdateFranchiseTask({ id: data?.id, data: values })).unwrap();
          dispatch(taskActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT && props.open && data) {
      form.setFieldValue('name', cast(data?.name));
      form.setFieldValue('description', cast(data?.description));
      form.setFieldValue('statusId', cast(data?.statusId));
      form.setFieldValue('dueDate', cast(data?.dueDate));
    }

    if (mode === DIALOG_MODE_CREATE && props.open) {
      form.setFieldValue('statusId', statusOptions[0]?.id || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, data]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
  };

  const handleCancel = () => {
    form.resetForm();
    handleSetOpen(false);
  };

  return (
    <Dialog open={props.open} onClose={() => handleCancel()} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addNew') : t('edit')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.name')}</InputLabel>
              <TextField
                name='name'
                placeholder={t('properties.name')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full '>
              <InputLabel>{t('addTaskDialog.status', { ns: 'admin/job' })}</InputLabel>
              <BaseSelect
                options={statusOptions}
                name='statusId'
                isHideIconCheckbox={true}
                value={form.values.statusId}
                onChange={(e) => {
                  form.setFieldValue('statusId', e.target.value);
                }}
                error={Boolean(form.errors?.statusId)}
                helperText={form.errors?.statusId}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('addTaskDialog.duedate', { ns: 'admin/job' })}</InputLabel>
              <BaseDatePicker
                placeholder={t('addTaskDialog.duedate', { ns: 'admin/job' })}
                value={form.values.dueDate}
                dateFormat='DD MMM, YYYY hh:mm a'
                onChange={(date) => {
                  if (date) {
                    const selectedDate = dayjs(date).format('YYYY-MM-DD HH:mm:ss');
                    form.setFieldValue('dueDate', selectedDate);
                  }
                }}
                popperClassName='fixed-popper'
                error={Boolean(form.errors.dueDate)}
                helperText={form.errors.dueDate}
                showTimeInput
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.description', { ns: 'admin/task' })}</InputLabel>
              <TextField
                name='description'
                placeholder={t('properties.description', { ns: 'admin/task' })}
                value={form.values?.description}
                onChange={form.handleChange}
                error={Boolean(form.errors?.description)}
                helperText={form.errors?.description}
                multiline
              />
            </Box>
          </BaseRow>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditFranchiseTaskDialog);
